/**
 * Peer rank/level API (specs/rank-on-social-screens). The server derives level + rank from
 * the ledger and sends nothing else about a peer.
 */

import { API_ENDPOINTS } from '../../../constants/api';
import { XP_BATCH_MAX_USER_IDS, type PlayerRank } from '../constants/playerRanks.constants';

type ApiClient = (
  endpoint: string,
  options?: Record<string, unknown>,
) => Promise<{ status: number; data: unknown }>;

export type PlayerRankMap = Record<string, PlayerRank>;

type BatchEnvelope = {
  success?: boolean;
  data?: Record<string, Partial<PlayerRank>>;
  error?: string;
};

/**
 * Level + rank for up to XP_BATCH_MAX_USER_IDS players.
 *
 * NEVER rejects (AC-14): badges are decoration on screens that are already correct without
 * them, so a 404 from a server predating this endpoint, a 500, or a dead network all resolve
 * to `{}` and the screens simply render their no-data state. A throw here would surface a
 * React Query error state on four working screens.
 */
export async function fetchPlayerRanks(
  apiClient: ApiClient,
  userIds: string[],
): Promise<PlayerRankMap> {
  const distinct = [...new Set(userIds)].slice(0, XP_BATCH_MAX_USER_IDS);
  if (distinct.length === 0) return {};

  try {
    const response = await apiClient(API_ENDPOINTS.users.xpBatch, {
      method: 'POST',
      data: { userIds: distinct },
    });
    const body = response.data as BatchEnvelope;
    if (response.status >= 400 || !body?.success || body.data == null) return {};

    const ranks: PlayerRankMap = {};
    for (const [userId, value] of Object.entries(body.data)) {
      if (typeof value?.level === 'number' && typeof value?.rank === 'number') {
        ranks[userId] = { level: value.level, rank: value.rank };
      }
    }
    return ranks;
  } catch {
    return {};
  }
}
